import { useState, useEffect, useCallback } from 'react';
import { SecureGet, SecurePost, SecurePut } from '../services/axiosCall';
import apis from '../services/Apis';
import useEvaluationRequestScope, { scopedEvaluationResults } from './useEvaluationRequestScope';


const useEvaluationSemester = ({
  url,
  formId,
  // userDetails,
  selectedYear,
  messageApi,
  convertToTableData,
  convertToRequestData,
  // convertPlanningToRequestData,
  rowUser
}) => {


  const scopeKey = JSON.stringify([rowUser, selectedYear, formId, url]);
  const scope = useEvaluationRequestScope(scopeKey);
  const [loadedScope, setLoadedScope] = useState(null);
  const visible = loadedScope === scopeKey;

  // State to store the unique identifier for the plan
  // const [id, setId] = useState(null);
  // const handleId = (id) => setId(id);

  const [fetchLoading, setFetchLoading] = useState(false);
  const handleFetchLoading = (loading) => setFetchLoading(loading);


  const [semesterLoading, setSemesterLoading] = useState({});
  const handleSemesterLoading = (semester, type, loading) => {
    setSemesterLoading((prev) => ({
      ...prev,
      [semester]: {
        ...prev[semester],
        [type]: loading,
      },
    }));
  };

  const [planing, setPlaning] = useState([]);
  const handlePlaning = (plan) => setPlaning(plan);

  const [semesterComments, setSemesterComments] = useState({});
  const [cumulativeApaPoints, setCumulativeApaPoints] = useState(0)


  // Fetch only the selected faculty/year/form; empty admin results stay empty.
  const fetchPlaning = useCallback((year, requestedForm) => {
    const isCurrent = scope.begin();
    handlePlaning([]);
    setSemesterComments({});
    setCumulativeApaPoints(0);
    setLoadedScope(scopeKey);
    setSemesterLoading({});
    if (!rowUser || !year || !requestedForm) {
      handleFetchLoading(false);
      return;
    }
    handleFetchLoading(true);
    SecureGet({ url, params: { year, apa_form: requestedForm, user: rowUser, limit: 1000 } })
      .then(async (response) => {
        if (!isCurrent()) return;
        const results = scopedEvaluationResults(response, rowUser, year, requestedForm);
        // Null JSON is not evaluation data and must not reach table converters.
        const usable = results.filter((record) => record?.json != null);
        const rows = convertToTableData ? convertToTableData({ ...response?.data, results: usable }) : usable;
        handlePlaning(rows);
        const comments = {};
        rows.forEach((plan) => { comments[plan.semester] = plan.comment || ''; });
        setSemesterComments(comments);
        if (usable.length) calculatePlaning(requestedForm);
      })
      .catch(() => {
        if (!isCurrent()) return;
        handlePlaning([]);
      })
      .finally(() => { if (isCurrent()) handleFetchLoading(false); });
  // Converters are section-level functions. Scope is represented by scopeKey.
  }, [scopeKey, rowUser, url, convertToTableData]);

  const updatePlaning = (semester) => {
    const semesterPlan = planing.find((plan) => plan.semester === semester);
    if (!visible || !semesterPlan) return;

    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    handleSemesterLoading(semester, 'updateLoading', true);

    const newPlan = {
      apa_form: formId,
      year: selectedYear,
      semester: semester,
      json: convertToRequestData ? convertToRequestData(planing, selectedYear, formId, semester).json : planing,
      // user: userDetails,
    };

    if (semesterPlan.id) {
      SecurePut({
        url: `${url}/${semesterPlan.id}`,
        data: { ...newPlan },
      })
        .then(() => {
          if (!isCurrent()) return;
          fetchPlaning(selectedYear, formId);

          handleSemesterLoading(semester, 'updateLoading', false);
          messageApi.success('Successfully updated');
        })
        .catch((error) => {
          if (!isCurrent()) return;
          handleSemesterLoading(semester, 'updateLoading', false);
          const { response } = error;
          const { data } = response || {};
          const { error: errorMsg } = data || {};

          if (errorMsg) messageApi.success(errorMsg);
          else messageApi.success("Something went wrong")
        });
    } else {
      SecurePost({
        url: `${url}`,
        data: { ...newPlan },
      })
        .then(() => {
          if (!isCurrent()) return;
          fetchPlaning(selectedYear, formId);

          handleSemesterLoading(semester, 'updateLoading', false);
          messageApi.success('Successfully updated');
        })
        .catch((error) => {
          if (!isCurrent()) return;
          handleSemesterLoading(semester, 'updateLoading', false);
          const { response } = error;
          const { data } = response || {};
          const { error: errorMsg } = data || {};

          if (errorMsg) messageApi.success(errorMsg);
          else messageApi.success("Something went wrong")
        });
    }
  };

  const calculatePlaning = (ID) => {
    if (!ID || !rowUser || !selectedYear) return;
    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    SecureGet({
      url: `${apis.CUMUlATIVE_APA}/${ID}/${selectedYear}`,
      params: { user: rowUser },
    })
      .then((response) => {
        if (isCurrent()) setCumulativeApaPoints(response?.data ?? 0);
      })
      .catch(() => { if (isCurrent()) setCumulativeApaPoints(0); });
  };

  const evaluatePlaning = (semester, selectedStatus, comment) => {
  
    const semesterPlan = planing.find((plan) => plan.semester === semester);
    if (!visible || !semesterPlan) return;

    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    handleSemesterLoading(semester, 'evaluateLoading', true);

    const newPlan = {
      id: semesterPlan.id,
      status: selectedStatus,
      type: 2,
      comment: comment
    };

    if (semesterPlan.id) {
      SecurePost({
        url: `${apis.EVALUATION_FORM}`,
        data: { ...newPlan },
      })
        .then(() => {
          if (!isCurrent()) return;
          fetchPlaning(selectedYear, formId);
          // setStatus(response?.data?.results[0]?.status)
          handleSemesterLoading(semester, 'evaluateLoading', false);
          messageApi.success('Evaluation updated successfully');
        })
        .catch((error) => {
          if (!isCurrent()) return;
          handleSemesterLoading(semester, 'evaluateLoading', false);
          const { response } = error;
          const { data } = response || {};
          const { error: errorMsg } = data || {};

          if (errorMsg) messageApi.success(errorMsg);
          else messageApi.success("Something went wrong")
        });
    }
  }

  useEffect(() => {
    fetchPlaning(selectedYear, formId);
  }, [fetchPlaning, formId, selectedYear, rowUser]);


  return {
    planing: visible ? planing : [],
    fetchLoading: !visible || fetchLoading,
    updatePlaning,
    handlePlaning,
    evaluatePlaning,
    cumulativeApaPoints: visible ? cumulativeApaPoints : 0,
    semesterLoading,
    semesterComments: visible ? semesterComments : {}
  };
};

export default useEvaluationSemester;
